import { useId, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import { SupportPrompt } from '../support/SupportPrompt';
import { MOODS, moodById, type MoodActionKind, type MoodId } from './moods';

interface MoodCheckInProps {
  value: MoodId | null;
  onChange: (mood: MoodId) => void;
  /** Not needed when `showResponse` is off. */
  onAction?: (kind: MoodActionKind) => void;
  /**
   * The line and next step after an answer. Off on the hub, where "For right
   * now" carries them instead.
   */
  showResponse?: boolean;
  /**
   * The group's name. A heading can sit inside it, so on the hub the
   * question IS the legend - read once, as both the outline entry and the
   * name of the eight choices.
   */
  legend?: ReactNode;
}

/**
 * "How are you feeling?" as a set of emotion words.
 *
 * Native radios under the chips: single choice, arrow keys between options
 * and "Anxious, 5 of 8" for free, with no roving-tabindex machinery to keep
 * in step. A low answer brings the support panel in under the suggestion
 * rather than replacing it - the student still gets the next small step.
 */
export function MoodCheckIn({
  value,
  onChange,
  onAction,
  legend = 'Pick the word that fits best.',
  showResponse = true,
}: MoodCheckInProps) {
  const name = useId();
  const mood = moodById(value);

  return (
    <div className='space-y-4'>
      <fieldset>
        <legend className='mb-4 text-sm text-muted-foreground'>{legend}</legend>
        <div className='flex flex-wrap gap-2'>
          {MOODS.map(option => {
            const checked = option.id === value;
            return (
              <label
                key={option.id}
                className={cn(
                  'relative cursor-pointer select-none rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-base',
                  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background',
                  checked
                    ? // Solid, like the active nav pill: the pale tint read as unselected.
                      'bg-primary text-primary-foreground shadow-elevation-1 dark:bg-brand-300 dark:text-brand-950'
                    : 'bg-card/70 text-foreground shadow-elevation-1 backdrop-blur-sm hover:bg-card dark:bg-card/60 dark:hover:bg-card'
                )}
              >
                <input
                  type='radio'
                  name={name}
                  value={option.id}
                  checked={checked}
                  onChange={() => onChange(option.id)}
                  className='sr-only'
                />
                {option.label}
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* Polite: the reply follows the student's own choice. */}
      {showResponse && (
        <div aria-live='polite'>
          {mood && (
            <div className='space-y-3'>
              <p className='text-sm text-foreground'>{mood.response}</p>
              {mood.action.kind !== 'support' && (
                <Button
                  variant='outline'
                  size='sm'
                  className='max-sm:h-11'
                  onClick={() => onAction?.(mood.action.kind)}
                >
                  {mood.action.label}
                  <ArrowRight className='ml-1.5 h-4 w-4' aria-hidden='true' />
                </Button>
              )}
              {mood.low && <SupportPrompt />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
